import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IInitialState } from '../../../../reduxService';
/* import { ISectorResponse, ISubSectorResponse } from '../../api/IApiResponse'; */
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
/* import { IMaterialResponse } from '../../../api/IApiResponse'; */
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { IMeshForm } from '../../components/InputForm/Mesh/IMesh';
import { ISafetyCoefficientsForm } from '../../components/InputForm/SafetyCoefficients/ISafetyCoefficients';
import { ICrestForm } from '../../components/InputForm/Crest/Crest';
/* import { IFetchTemplatesPayload } from '../../../api/common'; */
import { RootState } from '../../../../reduxService';
import { useSelector } from 'react-redux';
import { IMacro2ProjectPutData } from '../../../api/IApiRequest';
import { IMacro2ProjectGetData } from '../../../api/IApiResponse';
import { putMacro2ProjectAsync } from '../../../api/store/BeMaccaferriStoreHelper';
import { IRockSlopeForm } from '../../components/InputForm/RockSlope/IRockSlope';
import { RockSlopeDefault } from '../../components/InputForm/RockSlope/RockSlope';
import { MeshDefault } from '../../components/InputForm/Mesh/Mesh';
import { CrestDefault } from '../../components/InputForm/Crest/Crest';
import { SafetyCoefficientsDefault } from '../../components/InputForm/SafetyCoefficients/SafetyCoefficients';
import { convertTo, EConversionSIToImperial } from '../../../conversion';

export interface IProjectMacro2 {
    rockSlope?: IRockSlopeForm;
    mesh?: IMeshForm;
    safetyCoefficients?: ISafetyCoefficientsForm;
    crest?: ICrestForm;
    siteEnvironmentalLevelId?: string;
}

const checkConversion = (unit: string | undefined, value: number, conversion: EConversionSIToImperial) => {
    return unit == 'Metric' ? value : convertTo(value, conversion);
}

export const projectToData = (data: IProjectMacro2WithInfo): IMacro2ProjectPutData => {
    //console.log("convert to data MACRO2", data);
    return {
        id: data.projectId ?? data.projectInfo?.projectId,
        siteEnvironmentalLevelId: data.siteEnvironmentalLevelId,
        unit: data.projectInfo?.unit,
        meshDatas: {
            meshTypeId: data.mesh?.meshType,
            meshTypeName: data.mesh?.meshTypeName,
            meshUltimateTensileStrength: Number(data.mesh?.tensileResistance),
            meshUnitWeight: Number(data.mesh?.steelMeshUnitWeight),
            meshSafetyFactorForMeshStrength: Number(data.safetyCoefficients?.reductionForMeshResistance)
        },
        slopeDatas: {
            debrisAccumulationHeight: Number(data.rockSlope?.debrisAccumulationHeight),
            debrisAccumulationWidth: Number(data.rockSlope?.debrisAccumulationWidth),
            slopeInclination: Number(data.rockSlope?.inclination),
            debrisFrictionAngle: Number(data.rockSlope?.debrisFrictionAngle),
            debrisUnitWeight: Number(data.rockSlope?.debrisUnitWeight),
            safetyFactorForVariableLoads: Number(data.safetyCoefficients?.coefficientForVariableLoads),
            frictionAngleBetweenMeshAndSlope: Number(data.rockSlope?.meshSlopeFrictionAngle),
            safetyFactorForPermanentLoad: Number(data.safetyCoefficients?.coefficientForPermanentLoads),
            slopeTotalHeight: Number(data.rockSlope?.slopeTotalHeight)
        },
        snowDatas: {
            enableSnow: data.rockSlope?.snow,
            snowThickness: Number(data.rockSlope?.snowThickness),
            snowUnitWeightMeasuredVertically: Number(data.rockSlope?.snowUnitWeight)
        },
        topLongitudinalCableDatas: {
            anchorSpacingMeasuredHorizontalyX: Number(data.crest?.horizontalAnchorSpacing),
            safetyFactorOnXSpacing: Number(data.safetyCoefficients?.reductionOnXSpacing),
            anchorSpacingMeasuredOnTheMaximumSlopeGradientY: 0 /*Number(data.crest?.verticalAnchorSpacing)*/,
            safetyFactorOnVerticalGradientY: Number(data.safetyCoefficients?.reductionOnYGradient)
        },
        cableTypeDatas: {
            isCompiledCrestRopeSpecification: !data.crest?.customRope,
            cableId: data.crest?.ropeDiameterId,
            cableDiameter: Number(data.crest?.ropeDiameter),
            cableSteelId: data.crest?.ropeSteelGradeId,
            cableSteelGrade: Number(data.crest?.ropeSteelGrade),
            cableCoreTypeId: data.crest?.ropeCoreType,
            cableCoreType: data.crest?.ropeCoreTypeName,
            cableStrengthReductionCoefficient: Number(data.safetyCoefficients?.reductionForCableResistance),
            cableUltimateTensileStrength: Number(data.crest?.ropeUltimateTensileStrength)
        },
        anchorTypeDatas: {
            isCompiledCrestAnchorSpecification: !data.crest?.customAnchor,
            anchorTypeId: data.crest?.anchorType,
            anchorTypeName: data.crest?.anchorTypeName,
            barInternalDiameter: Number(data.crest?.anchorInternalDiameter),
            barExternalDiameter: Number(data.crest?.anchorExternalDiameter),
            thicknessOfCorrosionCrown: Number(data.crest?.crownThickness),
            barYieldStress: Number(data.crest?.anchoryieldStress),
            reductionCoefficientForSteelStrength: Number(data.safetyCoefficients?.coefficientSteelBarYield),
            rockGroutAdhesion: Number(data.crest?.rockGroutAdhesion),
            safetyCoefficientForReductionOfRockGroutAdhesion: Number(data.safetyCoefficients?.coefficientRockGroutAdesion),
            drillingDiameter: Number(data.crest?.drillingDiameter),
        },
        groundPlasticizationDatas: {
            assumedLengthOfGroundPlasticization: Number(data.crest?.assumedPlasticizationBetweenRockAndAnchor)
        },
    };
}

export const dataToProject = (data: IMacro2ProjectGetData): IProjectMacro2WithInfo => {
    const pUnit = data?.projectInfos?.unit ?? 'Metric';
    return {
        projectId: data.projectId,
        siteEnvironmentalLevelId: data.siteEnvironmentalLevelId,
        projectInfo: {
            projectId: data.projectId,
            unit: data?.projectInfos?.unit ?? 'Metric',
            projectName: data?.projectInfos?.projectName ?? '',
            projectType: data?.projectInfos?.projectType ?? 'macro2',
            nrProject: data?.projectInfos?.nrProject ?? '0',
            owner: data?.projectInfos?.owner ?? '',
            designerFirmName: data?.projectInfos?.designerFirmName ?? '',
            designerFirmCountry: data?.projectInfos?.designerFirmCountry ?? '',
            designerFirmCity: data?.projectInfos?.designerFirmCity ?? '',
            coordinates: data?.projectInfos?.coordinates ?? [undefined, undefined],
            productCountry: data?.projectInfos?.productCountry ?? null,
            notes: data?.projectInfos?.notes ?? '',
            mapImage: data?.projectInfos?.mapImage ?? '',
        },
        readOnly: data?.readOnly,
        hasBeenRan: data.hasBeenRan,
        rockSlope: {
            debrisAccumulationHeight: data?.slopeDatas?.debrisAccumulationHeight ?? checkConversion(pUnit, RockSlopeDefault.debrisAccumulationHeight, EConversionSIToImperial.m_to_ft),
            debrisAccumulationWidth: data?.slopeDatas?.debrisAccumulationWidth ?? checkConversion(pUnit, RockSlopeDefault.debrisAccumulationHeight, EConversionSIToImperial.m_to_ft),
            debrisAccumulationAngle: RockSlopeDefault.debrisAccumulationAngle,
            inclination: data?.slopeDatas?.slopeInclination  ?? RockSlopeDefault.inclination,
            debrisFrictionAngle: data?.slopeDatas?.debrisFrictionAngle  ?? RockSlopeDefault.debrisFrictionAngle,
            debrisUnitWeight: data?.slopeDatas?.debrisUnitWeight ?? checkConversion(pUnit, RockSlopeDefault.debrisUnitWeight, EConversionSIToImperial.kNm3_to_lbfft3),
            snow: data?.snowDatas?.enableSnow ?? RockSlopeDefault.snow,
            snowThickness: data?.snowDatas?.snowThickness ?? checkConversion(pUnit, RockSlopeDefault.snowThickness, EConversionSIToImperial.m_to_ft),
            snowUnitWeight: data?.snowDatas?.snowUnitWeightMeasuredVertically ?? checkConversion(pUnit, RockSlopeDefault.snowUnitWeight, EConversionSIToImperial.kNm3_to_lbfft3),
            meshSlopeFrictionAngle: data?.slopeDatas?.frictionAngleBetweenMeshAndSlope ?? RockSlopeDefault.meshSlopeFrictionAngle,
            slopeTotalHeight: data?.slopeDatas?.slopeTotalHeight ?? checkConversion(pUnit, RockSlopeDefault.slopeTotalHeight, EConversionSIToImperial.m_to_ft)
        },
        mesh: {
            meshType: data?.meshDatas?.meshTypeId ?? MeshDefault.meshType,
            meshTypeName: data?.meshDatas?.meshTypeName ?? MeshDefault.meshTypeName,
            tensileResistance: data?.meshDatas?.meshUltimateTensileStrength ?? MeshDefault.meshType,
            steelMeshUnitWeight: data?.meshDatas?.meshUnitWeight ?? MeshDefault.meshType
        },
        safetyCoefficients: {
            coefficientForVariableLoads: data?.slopeDatas?.safetyFactorForVariableLoads ?? SafetyCoefficientsDefault.coefficientForVariableLoads,
            coefficientForPermanentLoads: data?.slopeDatas?.safetyFactorForPermanentLoad ?? SafetyCoefficientsDefault.coefficientForPermanentLoads,
            reductionForMeshResistance: data?.meshDatas?.meshSafetyFactorForMeshStrength ?? SafetyCoefficientsDefault.reductionForMeshResistance,
            reductionForCableResistance: data?.cableTypeDatas?.cableStrengthReductionCoefficient ?? SafetyCoefficientsDefault.reductionForCableResistance,
            reductionOnXSpacing: data?.topLongitudinalCableDatas?.safetyFactorOnXSpacing ?? SafetyCoefficientsDefault.reductionOnXSpacing,
            reductionOnYGradient: data?.topLongitudinalCableDatas?.safetyFactorOnVerticalGradientY ?? SafetyCoefficientsDefault.reductionOnYGradient,
            coefficientSteelBarYield: data?.anchorTypeDatas?.reductionCoefficientForSteelStrength ?? SafetyCoefficientsDefault.coefficientSteelBarYield,
            coefficientRockGroutAdesion: data.anchorTypeDatas?.safetyCoefficientForReductionOfRockGroutAdhesion ?? SafetyCoefficientsDefault.coefficientRockGroutAdesion
        },
        crest: {
            horizontalAnchorSpacing: data?.topLongitudinalCableDatas?.anchorSpacingMeasuredHorizontalyX ?? checkConversion(pUnit, CrestDefault.horizontalAnchorSpacing, EConversionSIToImperial.m_to_ft),
            //verticalAnchorSpacing: data?.topLongitudinalCableDatas?.anchorSpacingMeasuredOnTheMaximumSlopeGradientY ?? checkConversion(pUnit, CrestDefault.verticalAnchorSpacing, EConversionSIToImperial.m_to_ft),
            ropeDiameter: data?.cableTypeDatas?.cableDiameter?.toFixed(2) ?? CrestDefault.ropeDiameter,
            ropeDiameterId: data?.cableTypeDatas?.cableId ?? CrestDefault.ropeDiameterId,
            ropeSteelGrade: data?.cableTypeDatas?.cableSteelGrade ?? checkConversion(pUnit, CrestDefault.ropeSteelGrade, EConversionSIToImperial.MPa_to_lbfft2),
            ropeSteelGradeId: data?.cableTypeDatas?.cableSteelId ?? CrestDefault.ropeSteelGradeId,
            ropeCoreType: data?.cableTypeDatas?.cableCoreTypeId ?? CrestDefault.ropeCoreType,
            ropeCoreTypeName: data?.cableTypeDatas?.cableCoreType ?? CrestDefault.ropeCoreTypeName,
            customRope: !(data?.cableTypeDatas?.isCompiledCrestRopeSpecification ?? !CrestDefault.customRope),
            ropeUltimateTensileStrength: data?.cableTypeDatas?.cableUltimateTensileStrength ?? CrestDefault.ropeUltimateTensileStrength,
            customAnchor: !(data?.anchorTypeDatas?.isCompiledCrestAnchorSpecification ?? !CrestDefault.customAnchor),
            anchorType: data?.anchorTypeDatas?.anchorTypeId ?? CrestDefault.anchorType,
            anchorTypeName: data?.anchorTypeDatas?.anchorTypeName ?? CrestDefault.anchorTypeName,
            anchorInternalDiameter: data?.anchorTypeDatas?.barInternalDiameter ?? CrestDefault.anchorInternalDiameter,
            anchorExternalDiameter: data?.anchorTypeDatas?.barExternalDiameter ? data?.anchorTypeDatas?.barExternalDiameter.toFixed(2) :  CrestDefault.anchorExternalDiameter,
            drillingDiameter: data.anchorTypeDatas?.drillingDiameter ?? CrestDefault.drillingDiameter,
            automaticCrownThickness: CrestDefault.automaticCrownThickness,
            crownThickness: data?.anchorTypeDatas?.thicknessOfCorrosionCrown ?? CrestDefault.crownThickness,
            environmentalAggressivity: CrestDefault.environmentalAggressivity,
            lifespanYears: CrestDefault.lifespanYears,
            anchoryieldStress: data?.anchorTypeDatas?.barYieldStress ?? checkConversion(pUnit, CrestDefault.anchoryieldStress, EConversionSIToImperial.MPa_to_ksi),
            NFP94270: CrestDefault.NFP94270,
            rockGroutAdhesion: data?.anchorTypeDatas?.rockGroutAdhesion ?? checkConversion(pUnit, CrestDefault.rockGroutAdhesion, EConversionSIToImperial.MPa_to_psi),
            assumedPlasticizationBetweenRockAndAnchor: data?.groundPlasticizationDatas?.assumedLengthOfGroundPlasticization ?? CrestDefault.assumedPlasticizationBetweenRockAndAnchor,
        }
    }
}

export interface IProjectMacro2WithInfo extends IProjectMacro2 {
    projectId?: string;
    projectInfo?: IProjectInfoForm;
    readOnly?: boolean;
    hasBeenRan?: boolean;
    disableRun?: boolean;
}

const initialState: IInitialState<IProjectMacro2WithInfo> = {
    initialState: true,
};

export interface IMaterialSectorSlice {
    sector: number;
    materialId: number;
}
export interface IMaterialSubSectorSlice {
    sectorId: number;
}

export const projectSlice = createSlice({
    name: 'projectMacro2',
    initialState,
    reducers: {
        cleanProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        setProject: (state, action: PayloadAction<IProjectMacro2WithInfo>) => {
            state.initialState = false;
            state.data = action.payload;
        },
        setProjectInfo: (state, action: PayloadAction<IProjectInfoForm>) => {
            state.initialState = false;
            state.data = { ...state.data, projectInfo: action.payload } as IProjectMacro2WithInfo;
        },
        setRockSlope: (state, action: PayloadAction<IRockSlopeForm>) => {
            state.initialState = false;
            state.data = { ...state.data, rockSlope: action.payload };
        },
        setMesh: (state, action: PayloadAction<IMeshForm>) => {
            state.initialState = false;
            state.data = { ...state.data, mesh: action.payload };
        },
        setSafetyCoefficients: (state, action: PayloadAction<ISafetyCoefficientsForm>) => {
            state.initialState = false;
            state.data = { ...state.data, safetyCoefficients: action.payload };
        },
        setCrest: (state, action: PayloadAction<ICrestForm>) => {
            state.initialState = false;
            state.data = { ...state.data, crest: action.payload };
        },
        setSiteEnvLevel: (state, action: PayloadAction<string>) => {
            state.initialState = false;
            state.data = { ...state.data, siteEnvironmentalLevelId: action.payload };
        },
        setDebrisAccumulationAngle: (state, action: PayloadAction<number>) => {
            state.initialState = false;
            if (state.data && state.data.rockSlope) {
                state.data.rockSlope.debrisAccumulationAngle = action.payload;
                //console.log(state.data.rockSlope.debrisAccumulationAngle)
            }
        },
        setReadOnly: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, readOnly: action.payload } as IProjectMacro2WithInfo;
        },
        setHasBeenRan: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, hasBeenRan: action.payload } as IProjectMacro2WithInfo;
        },
        setDisableRun: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, disableRun: action.payload } as IProjectMacro2WithInfo;
        },
    },
});

export const committedProjectSlice = createSlice({
    name: 'committedProjectMacro2',
    initialState,
    reducers: {
        cleanCommittedProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        commitProject: (state, project: PayloadAction<IProjectMacro2WithInfo>) => {
            //console.log('commitProject');
            state.data = { ...project.payload };
        }
    },
});


export const { setProject, cleanProject, setProjectInfo, setRockSlope, setCrest, setDebrisAccumulationAngle, setMesh, setSafetyCoefficients, setSiteEnvLevel, setReadOnly, setHasBeenRan, setDisableRun } =
    projectSlice.actions;

export const { cleanCommittedProject, commitProject } = committedProjectSlice.actions;

export default projectSlice.reducer;
